import { useEffect, useRef, useState } from 'react';
import { cn } from '@/shared/lib/utils';
import { _t } from '@/i18n/t';
import { siteRowMs } from '../utils/scanProgress';

/** Tall enough to mask at both edges, matching the reference's read window. */
const STREAM_ROW_HEIGHT = 44;
const MIN_STREAM_ROWS = 7;
/** The window is 264px tall, so the row under the read head starts three in. */
const STREAM_HEIGHT = 264;
const CENTRE_ROW = Math.floor(STREAM_HEIGHT / 2 / STREAM_ROW_HEIGHT);
const DISCOVERY_HOLD_MS = 4_000;

interface PageEntry {
  url: string;
  title: string;
}

export interface SiteScanData {
  pages: PageEntry[];
  totalPages: number;
  imageCount: number;
  totalImages: number;
  firstImageUrl: string | null;
  imageUrls: string[];
  imagesDone: boolean;
  discoveryLog: string[];
}

interface Props {
  data: SiteScanData;
  pagesFinal?: boolean;
  onPlaybackComplete?: () => void;
}

export default function SiteScanPanel({ data, pagesFinal = false, onPlaybackComplete }: Props) {
  const { pages, discoveryLog } = data;
  const showPages = usePagesTakeOver(pages.length > 0, discoveryLog.length > 0);

  return (
    <div className="ob-site" data-testid="onboarding-scan-site">
      {showPages ? (
        <PageStream
          pages={pages}
          totalPages={data.totalPages}
          pagesFinal={pagesFinal}
          onPassComplete={onPlaybackComplete}
        />
      ) : discoveryLog.length > 0 ? (
        <DiscoveryStream findings={discoveryLog} />
      ) : (
        <SiteConnecting />
      )}
      {pages.length > 0 && (
        <p
          className="ob-site__count text-muted-foreground"
          data-testid="onboarding-scan-site-count"
        >
          {pages.length === 1
            ? _t('onboarding.crawl.chapter.site.pageRead')
            : _t('onboarding.crawl.chapter.site.pagesRead', { count: String(pages.length) })}
        </p>
      )}
    </div>
  );
}

function SiteConnecting() {
  return (
    <div className="ob-site-init" role="status">
      <span className="ob-site-init__row">
        <span className="ob-site-init__orbit" aria-hidden="true">
          <span />
        </span>
        <span className="ob-site-init__title text-foreground">
          {_t('onboarding.crawl.chapter.site.connecting')}
        </span>
      </span>
      <span className="ob-site-init__sub text-muted-foreground">
        {_t('onboarding.crawl.chapter.site.finding')}
        <span className="ob-site-init__dots" aria-hidden="true">
          <span>.</span>
          <span>.</span>
          <span>.</span>
        </span>
      </span>
    </div>
  );
}

function usePagesTakeOver(pagesArrived: boolean, hadFindings: boolean): boolean {
  const [showPages, setShowPages] = useState(false);
  const held = useRef(false);

  useEffect(() => {
    if (!pagesArrived || held.current) return;
    held.current = true;
    if (!hadFindings) {
      setShowPages(true);
      return;
    }
    const id = window.setTimeout(() => setShowPages(true), DISCOVERY_HOLD_MS);
    return () => window.clearTimeout(id);
  }, [pagesArrived, hadFindings]);

  return showPages;
}

function DiscoveryStream({ findings }: { findings: string[] }) {
  const offset = Math.max(0, findings.length - 1 - CENTRE_ROW) * STREAM_ROW_HEIGHT;

  return (
    <div
      className="ob-stream"
      data-testid="onboarding-scan-site-discoveries"
      role="status"
      aria-live="polite"
    >
      <div
        className="ob-stream__col ob-stream__col--stepped"
        style={{ transform: `translateY(${-offset}px)` }}
      >
        {findings.map((finding, index) => (
          <div
            className={cn(
              'ob-stream__row',
              index === findings.length - 1 && 'ob-stream__row--read',
            )}
            key={`${index}-${finding}`}
            style={{ height: STREAM_ROW_HEIGHT }}
          >
            <span className="ob-stream__title text-foreground">{finding}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PageStream({
  pages,
  totalPages,
  pagesFinal,
  onPassComplete,
}: {
  pages: PageEntry[];
  totalPages: number;
  pagesFinal: boolean;
  onPassComplete?: () => void;
}) {
  const filled: PageEntry[] = [];
  while (filled.length < MIN_STREAM_ROWS && pages.length > 0) filled.push(...pages);
  const rows = filled.concat(filled);

  const rowMs = siteRowMs(Math.max(totalPages, pages.length));
  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  const columnRef = useRef<HTMLDivElement>(null);
  const [rowsPassed, setRowsPassed] = useState(0);
  useEffect(() => {
    if (filled.length === 0 || reducedMotion) return;
    const scroll = columnRef.current?.getAnimations?.()[0];
    if (!scroll) {
      const id = window.setInterval(() => setRowsPassed((prev) => prev + 1), rowMs);
      return () => window.clearInterval(id);
    }
    let frame = 0;
    const follow = () => {
      setRowsPassed(Math.floor(Number(scroll.currentTime ?? 0) / rowMs));
      frame = window.requestAnimationFrame(follow);
    };
    frame = window.requestAnimationFrame(follow);
    return () => window.cancelAnimationFrame(frame);
  }, [filled.length, rowMs, reducedMotion]);
  const lit = CENTRE_ROW + rowsPassed;

  const passed = reducedMotion || rowsPassed >= pages.length;
  const reported = useRef(false);
  useEffect(() => {
    if (reported.current || !pagesFinal || !passed) return;
    reported.current = true;
    onPassComplete?.();
  }, [pagesFinal, passed, onPassComplete]);

  return (
    <div className="ob-stream ob-stream--pages" aria-hidden="true">
      <div
        ref={columnRef}
        className="ob-stream__col"
        style={{ animationDuration: `${filled.length * (rowMs / 1000)}s` }}
      >
        {rows.map((page, index) => (
          <div
            className={cn(
              'ob-stream__row',
              index % filled.length === lit % filled.length && 'ob-stream__row--read',
            )}
            key={`${index}-${page.url}`}
            style={{ height: STREAM_ROW_HEIGHT }}
          >
            <span className="ob-stream__title text-foreground">
              {page.title || pathOf(page.url)}
            </span>
            <span className="ob-stream__path font-mono text-muted-foreground">
              {pathOf(page.url)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function pathOf(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.pathname + parsed.search;
  } catch {
    const slash = url.indexOf('/');
    return slash >= 0 ? url.slice(slash) : url;
  }
}
